import { JellyView, type JellyState } from "./JellyView";
import { JellyTextures } from "./JellyTextures";
import type { Texture } from "pixi.js";
import { EventKind } from "../events/EventQueue";
import { Application, Container, Graphics } from "pixi.js";
import type { ContentCatalog } from "../content/types";
import {
  DROP_SPRITE_RADIUS,
  FIXED_STEP,
  PALETTE,
  WATER_PALETTE,
} from "../core/constants";
import type { GameContext } from "../core/GameContext";

interface SlimeVisual {
  jelly: JellyView;
  identity: number;
  definition: number;
  state: JellyState;
}

/** 压力状态的只读视图：体积驱动憋气脸切档，待爆标记驱动徽标。结构性接口，engine 不引用 mechanics 类型。 */
export interface PressureOverlay {
  readonly volume: Float32Array;
  readonly pending: Uint8Array;
}

/** 成对连线的只读视图。结构性接口而非引用 mechanics 的类型——engine 不得依赖 mechanics。 */
export interface LinkOverlay {
  readonly count: number;
  readonly a: Int16Array;
  readonly b: Int16Array;
}

/** 把 0xRRGGBB 按比例压暗，用来从身体色派生描边色。 */
function darken(color: number, factor: number): number {
  const r = Math.round(((color >> 16) & 0xff) * factor);
  const g = Math.round(((color >> 8) & 0xff) * factor);
  const b = Math.round((color & 0xff) * factor);
  return (r << 16) | (g << 8) | b;
}

/** 内框：世界边缘到圆角面板的留边与圆角。背景绘制与场地裁剪共用，两处不一致会露出一条缝。 */
const FRAME_INSET = 14;
const FRAME_RADIUS = 24;

/** 开局预建的视图数量。只是"多少个之前不必在运行中创建"，不是上限；超出后按需增长。 */
const VIEW_POOL_BASELINE = { drops: 32, particles: 256 } as const;

export class PixiRenderer {
  private readonly jellyTextures = new JellyTextures();
  private materials: Texture[] = [];
  private disposed = false;
  private initialized = false;
  private initializing = false;
  readonly app = new Application();
  readonly world = new Container();
  private readonly slimeLayer = new Container();
  private readonly dropLayer = new Container();
  private readonly particleLayer = new Container();
  private readonly playerLayer = new Container();
  private readonly slimeViews: (SlimeVisual | undefined)[] = [];
  private readonly dropViews: Graphics[] = [];
  private readonly particleViews: Graphics[] = [];
  private readonly player = new Container();
  private readonly turretBase = new Graphics();
  private readonly barrel = new Graphics();
  private readonly links = new Graphics();
  private elapsed = 0;
  /** 开火后坐：每发一滴水置 1，约 0.25 秒回位；持续开火即微抖。按 shotsFired 的增量触发，不依赖事件。 */
  private recoil = 0;
  private lastShots = 0;

  async init(host: HTMLElement, catalog: ContentCatalog): Promise<void> {
    if (this.disposed || this.initialized || this.initializing)
      throw new Error("渲染器不能重复初始化");
    this.initializing = true;
    try {
      const world = catalog.levels[0].world;
      await this.app.init({
        width: world.width,
        height: world.height,
        backgroundAlpha: 0,
        antialias: true,
        autoDensity: true,
        resolution: Math.min(2, window.devicePixelRatio || 1),
      });
      this.initialized = true;
      if (this.disposed) throw new Error("渲染器已销毁");
      this.materials = await Promise.all(
        catalog.slimes.map((definition) =>
          this.jellyTextures.prepare(definition.jelly),
        ),
      );
      if (this.disposed) throw new Error("渲染器已销毁");
      this.app.canvas.setAttribute(
        "aria-label",
        "Slime Pop: Jelly Defender game canvas",
      );
      this.app.canvas.dataset.testid = "game-canvas";
      host.appendChild(this.app.canvas);
      this.drawBackdrop(
        world.width,
        world.height,
        world.leakLine,
        world.playerRow,
      );
      // 场地层裁到内框：投放的史莱姆从画面上方入场，不裁的话会在内框之外的边框带里先冒出来。
      // 玩家层不裁——玩家本就在框内，且裁剪会让它被框边切掉一角。
      const field = new Container();
      field.addChild(
        this.links,
        this.slimeLayer,
        this.dropLayer,
        this.particleLayer,
      );
      const clip = new Graphics()
        .roundRect(
          FRAME_INSET,
          FRAME_INSET,
          world.width - FRAME_INSET * 2,
          world.height - FRAME_INSET * 2,
          FRAME_RADIUS,
        )
        .fill(0xffffff);
      field.mask = clip;
      this.world.addChild(field, clip, this.playerLayer);
      this.app.stage.addChild(this.world);
      this.createPools();
      this.drawPlayer();
    } catch (error) {
      this.destroy();
      throw error;
    } finally {
      this.initializing = false;
    }
  }

  destroy(): void {
    this.disposed = true;
    if (this.initialized) {
      this.app.destroy(true, { children: true });
      this.initialized = false;
    }
    this.slimeViews.length =
      this.dropViews.length =
      this.particleViews.length =
        0;
    this.materials.length = 0;
    this.jellyTextures.destroy();
  }

  render(
    context: GameContext,
    dt: number,
    links?: LinkOverlay,
    pressure?: PressureOverlay,
    advancePaused = false,
  ): void {
    this.elapsed += dt;
    const { slimes, drops, particles, state, catalog } = context;
    const world = context.level.world;
    // 黏连的占位视觉：一条淡线连起绑定的一对，画在史莱姆之下。真正的美术归 T017。
    this.links.clear();
    if (links !== undefined && links.count > 0) {
      for (let index = 0; index < links.count; index += 1) {
        const a = links.a[index];
        const b = links.b[index];
        this.links
          .moveTo(slimes.x[a], slimes.y[a])
          .lineTo(slimes.x[b], slimes.y[b]);
      }
      this.links.stroke({ color: WATER_PALETTE.water, width: 3, alpha: 0.3 });
    }
    const shake = state.shake;
    this.world.position.set(
      Math.sin(this.elapsed * 113) * shake,
      Math.cos(this.elapsed * 97) * shake * 0.7,
    );
    state.shake *= Math.pow(0.00008, dt);
    if (state.shake < 0.05) state.shake = 0;
    for (let id = 0; id < slimes.capacity; id += 1) {
      const active = slimes.active[id] !== 0;
      if (!active) {
        const view = this.slimeViews[id];
        if (view) view.jelly.container.visible = false;
        continue;
      }
      const view = this.ensureSlimeView(id, context);
      const definition = catalog.slimes[slimes.definition[id]];
      view.jelly.container.visible = true;
      view.jelly.container.position.set(slimes.x[id], slimes.y[id]);
      const base = definition.burstVolume * world.baseFraction;
      const input = view.state;
      input.fill = pressure
        ? (pressure.volume[id] - base) /
          Math.max(1, definition.burstVolume - base)
        : 0;
      input.pending = pressure !== undefined && pressure.pending[id] !== 0;
      input.radius = slimes.radius[id];
      input.restRadius = definition.radiusRest;
      input.seed = slimes.seed[id];
      input.scaleX = slimes.scaleX[id];
      input.scaleY = slimes.scaleY[id];
      view.jelly.setState(input);
      view.jelly.step(
        state.phase === "paused" && !advancePaused ? 0 : Math.min(1, dt),
      );
    }
    for (let id = 0; id < drops.capacity; id += 1) {
      const active = drops.active[id] !== 0;
      if (!active && id >= this.dropViews.length) continue;
      const view = this.ensureDropView(id);
      view.visible = active;
      if (!active) continue;
      const definition = catalog.drops[drops.definition[id]];
      view.position.set(drops.x[id], drops.y[id]);
      // 沿速度方向拉伸成水丝：长度取「相邻两滴的间距」，于是首尾相接、读作连续水流而非一串珠子。
      // 由速度与射速导出而不是写死——改任一参数时视觉自动跟上，不需要人记得同步。
      const pulse = 1 + Math.sin(this.elapsed * 18) * definition.pulse;
      const base = (definition.radius / DROP_SPRITE_RADIUS) * pulse;
      // 射击冷却按固定步递减，因此实际发射间隔被量化成固定步的整数倍——用标称间隔算
      // 会算短，水丝之间留下间隙。这里按量化后的真实间隔算。
      const interval = Math.max(
        FIXED_STEP,
        Math.ceil(world.fireInterval / FIXED_STEP) * FIXED_STEP,
      );
      const spacing = definition.speed * interval;
      const stretch = Math.max(1, spacing / (2 * definition.radius));
      view.rotation = Math.atan2(drops.vy[id], drops.vx[id]);
      view.scale.set(base * stretch, base);
      view.tint = definition.visualColor;
    }
    for (let id = 0; id < particles.capacity; id += 1) {
      const active = particles.active[id] !== 0;
      if (!active && id >= this.particleViews.length) continue;
      const view = this.ensureParticleView(id);
      view.visible = active;
      if (!active) continue;
      view.position.set(particles.x[id], particles.y[id]);
      view.scale.set(particles.size[id] / 5);
      view.tint = particles.color[id];
      view.alpha = Math.max(0, particles.life[id] / particles.maxLife[id]);
    }
    this.playerLayer.position.set(state.playerX, world.playerRow);
    const breathing = 1 + Math.sin(this.elapsed * 6) * 0.025;
    this.player.scale.set(breathing, 1 / breathing);
    // 关卡重开时 shotsFired 归零：只同步计数，不触发后坐。
    const shots = context.metrics.shotsFired;
    if (shots > this.lastShots) this.recoil = 1;
    this.lastShots = shots;
    this.recoil *= Math.pow(0.0005, dt);
    if (this.recoil < 0.01) this.recoil = 0;
    this.barrel.y = 4 * this.recoil;
    this.turretBase.scale.set(1 + 0.05 * this.recoil, 1 - 0.08 * this.recoil);
  }

  private drawBackdrop(
    width: number,
    height: number,
    defenceY: number,
    playerRow: number,
  ): void {
    const background = new Graphics();
    background.rect(0, 0, width, height).fill(PALETTE.ink);
    background
      .roundRect(
        FRAME_INSET,
        FRAME_INSET,
        width - FRAME_INSET * 2,
        height - FRAME_INSET * 2,
        FRAME_RADIUS,
      )
      .fill({ color: PALETTE.navy, alpha: 0.9 })
      .stroke({ color: 0x2c4a8a, width: 2 });
    for (let id = 0; id < 56; id += 1) {
      const x = 24 + ((id * 83) % (width - 48));
      const y = 24 + ((id * 137) % (height - 70));
      const size = id % 7 === 0 ? 2 : 1;
      background.circle(x, y, size).fill({
        color: id % 5 === 0 ? PALETTE.violet : PALETTE.cream,
        alpha: 0.25 + (id % 4) * 0.1,
      });
    }
    background
      .rect(24, defenceY - 2, width - 48, 3)
      .fill({ color: 0xff4f70, alpha: 0.75 });
    background
      .rect(24, defenceY + 2, width - 48, 1)
      .fill({ color: PALETTE.cream, alpha: 0.26 });
    // 炮台轨道：让横向移动读作「在轨道上滑」，也给炮台一个落脚处。
    background
      .rect(24, playerRow + 16, width - 48, 2)
      .fill({ color: 0x2c4a8a, alpha: 0.9 });
    background
      .rect(24, playerRow + 16, width - 48, 1)
      .fill({ color: WATER_PALETTE.water, alpha: 0.25 });
    this.world.addChild(background);
  }
  consumeHits(context: GameContext): void {
    const events = context.events;
    for (let i = 0; i < events.count; i++) {
      if (events.kind[i] !== EventKind.CollisionDetected) continue;
      const id = events.target[i];
      if (
        id < 0 ||
        !context.slimes.active[id] ||
        events.targetRecordingEntityId[i] !==
          context.slimes.recordingEntityId[id]
      )
        continue;
      this.ensureSlimeView(id, context).jelly.hit();
    }
  }

  /** Slots reuse a single view; all definitions have the same resource contract. */
  private ensureSlimeView(id: number, context: GameContext): SlimeVisual {
    const index = context.slimes.definition[id];
    const identity = context.slimes.recordingEntityId[id];
    const definition = context.catalog.slimes[index];
    let view = this.slimeViews[id];
    if (!view) {
      const jelly = new JellyView(
        definition.jelly.parameters,
        this.materials[index],
      );
      this.slimeLayer.addChild(jelly.container);
      view = this.slimeViews[id] = {
        jelly,
        definition: index,
        identity,
        state: {
          fill: 0,
          pending: false,
          radius: definition.radiusRest,
          scaleX: 1,
          scaleY: 1,
        },
      };
    } else {
      if (view.definition !== index) {
        view.jelly.configure(
          definition.jelly.parameters,
          this.materials[index],
        );
        view.definition = index;
        view.jelly.reset();
      }
      if (view.identity !== identity) {
        view.identity = identity;
        view.jelly.reset();
      }
    }
    return view;
  }
  private ensureDropView(id: number): Graphics {
    for (let next = this.dropViews.length; next <= id; next += 1) {
      // 半径 DROP_SPRITE_RADIUS 的白圆，运行时按定义半径与拉伸缩放。不画高光——
      // 水滴现在只有 2.5 半径，高光在拉伸后会变成一道歪斜的亮条。
      const view = new Graphics();
      view.circle(0, 0, DROP_SPRITE_RADIUS).fill(0xffffff);
      view.visible = false;
      this.dropLayer.addChild(view);
      this.dropViews.push(view);
    }
    return this.dropViews[id];
  }
  private ensureParticleView(id: number): Graphics {
    for (let next = this.particleViews.length; next <= id; next += 1) {
      const view = new Graphics();
      view.circle(0, 0, 4).fill(0xffffff);
      view.visible = false;
      this.particleLayer.addChild(view);
      this.particleViews.push(view);
    }
    return this.particleViews[id];
  }
  private createPools(): void {
    this.ensureDropView(VIEW_POOL_BASELINE.drops - 1);
    this.ensureParticleView(VIEW_POOL_BASELINE.particles - 1);
  }
  /**
   * 炮台是轨道炮台：方形底座坐在轨道上，半圆穹顶伸出一根根部粗、管口细的短炮管，管口一圈泡沫色。
   * 不画脸——与史莱姆拉开区分，亲和力交给史莱姆。底座与炮管分两层，后坐只动位移与缩放，不重画。
   */
  private drawPlayer(): void {
    const base = this.turretBase;
    const barrel = this.barrel;
    base
      .roundRect(-24, 2, 48, 14, 5)
      .fill(darken(WATER_PALETTE.deep, 0.7))
      .stroke({ color: WATER_PALETTE.water, width: 1.5, alpha: 0.8 });
    base
      .circle(-18, 12, 3)
      .fill({ color: WATER_PALETTE.foam, alpha: 0.6 })
      .circle(18, 12, 3)
      .fill({ color: WATER_PALETTE.foam, alpha: 0.6 });
    base
      .arc(0, 3, 20, Math.PI, 0)
      .closePath()
      .fill(WATER_PALETTE.deep)
      .stroke({ color: WATER_PALETTE.water, width: 2, alpha: 0.9 });
    base.ellipse(-8, -6, 7, 3).fill({ color: WATER_PALETTE.foam, alpha: 0.35 });
    barrel
      .poly([-7, -6, 7, -6, 5, -38, -5, -38])
      .fill(WATER_PALETTE.water)
      .stroke({ color: WATER_PALETTE.deep, width: 1.5, alpha: 0.9 });
    barrel.roundRect(-8, -43, 16, 7, 3.5).fill(WATER_PALETTE.foam);
    // 炮管在穹顶之下加入，穹顶盖住管根，读作从穹顶里伸出来。
    this.player.addChild(barrel, base);
    this.playerLayer.addChild(this.player);
  }
}
